import { Component } from 'react';
import { withAlert } from 'react-alert';
import FontAwesome from 'react-fontawesome';
import { connect } from 'react-redux';
import classNames from 'classnames';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';

import { sendQuestSolution } from '@lootboy/lib/slices/cards';
import { isPastAndTruthy } from '@lootboy/lib/utils/dateAndTime';

import Alert from '~/components/Alert';
import { OKAction } from '~/components/BootstrapDialog';
import LightButton from '~/components/LightButton';
import LootBoyButton from '~/components/LootBoyButton';
import LootCountdown from '~/components/LootCountdown';

import { pushDialog } from '~/redux/actions/AlertActions';
import { getImageUrl } from '~/utils';

import './style.css';

class LootGemQuestView extends Component {
  static propTypes = {
    alert: PropTypes.object.isRequired,
    cardId: PropTypes.string.isRequired,
    expiresAt: PropTypes.string,
    isUsernameRequired: PropTypes.bool,
    pushDialog: PropTypes.func.isRequired,
    reason: PropTypes.string,
    screenshot: PropTypes.string,
    sendQuestSolution: PropTypes.func.isRequired,
    // from mapDispatchToProps
    status: PropTypes.string.isRequired,
    username: PropTypes.string,
  };

  static defaultProps = {
    screenshot: '',
    username: '',
  };

  static STATUS_MAPPING = {
    IN_PROGRESS: 'LootGemQuestView_in_progress',
    REJECTED: 'LootGemQuestView_status_solution_rejected',
    VERIFICATION_REQUIRED: 'LootGemQuestView_status_verification_in_progress',
    COMPLETED: 'LootGemQuestView_status_completed',
    CANCELLED: 'LootGemQuestView_status_cancelled',
    EXPIRED: 'LootGemQuestView_status_time_ended',
  };

  static MAX_IMAGE_SIZE = 5 * 1024 * 1024;

  constructor(props) {
    super(props);

    this.state = {
      username: props.username,
      screenshot: getImageUrl(props.screenshot),
      uploadObject: null,
      countdownEnd: isPastAndTruthy(props.expiresAt),
      missingUsername: false,
      missingScreenshot: false,
      processing: false,
    };

    this.onSubmit.bind(this);
    this.onSubmitButtonPressed.bind(this);
    this.fileUpload.bind(this);
  }

  fileUpload(e) {
    e.preventDefault();

    const reader = new FileReader();

    const file = e.target.files[0];

    if (file.size > LootGemQuestView.MAX_IMAGE_SIZE) {
      this.props.pushDialog({
        body: I18n.t('Api_CardController_screenshot_too_big'),
        actions: [OKAction()],
      });

      return;
    }

    reader.onload = (event) => {
      this.setState({
        uploadObject: file,
        screenshot: event.target.result,
        missingScreenshot: false,
      });
    };
    reader.readAsDataURL(file);
  }

  onUsernameChanged(event) {
    const username = event.target.value;
    this.setState({
      username,
      missingUsername: !username.trim(),
    });
  }

  onSubmitButtonPressed() {
    if (this.state.processing) return;
    this.setState({ processing: true });
    this.onSubmit().finally(() => this.setState({ processing: false }));
  }

  async onSubmit() {
    const { cardId, isUsernameRequired, alert } = this.props;

    const { uploadObject, username } = this.state;

    if (isUsernameRequired) {
      if (!username || !username.trim()) {
        this.setState({ missingUsername: true });
      }
    }

    if (!uploadObject) {
      this.setState({ missingScreenshot: true });
    }

    if (
      (isUsernameRequired && (!username || !username.trim())) ||
      !uploadObject
    ) {
      this.props.pushDialog({
        body: I18n.t('ContactView_missing_data'),
        actions: [OKAction()],
      });

      return null;
    }

    if (uploadObject.size > LootGemQuestView.MAX_IMAGE_SIZE) {
      this.setState({ missingScreenshot: true });

      this.props.pushDialog({
        body: I18n.t('Api_CardController_screenshot_too_big'),
        actions: [OKAction()],
      });

      return null;
    }

    const files = [
      {
        index: 0,
        value: uploadObject,
      },
    ];

    return this.props
      .sendQuestSolution(cardId, { username }, files)
      .then(() =>
        alert.success(
          <Alert
            body={I18n.t('ContactView_successful_submit')}
            title={I18n.t('common_success')}
          />,
        ),
      )
      .catch((err) =>
        this.props.pushDialog({
          title: I18n.t('common_error'),
          body: err.message,
          actions: [OKAction()],
        }),
      );
  }

  renderCountdown() {
    const { expiresAt, status } = this.props;

    if (!expiresAt || status !== 'IN_PROGRESS') {
      return null;
    }

    if (this.state.countdownEnd) {
      return (
        <div className="countdownContainer">
          <h3>{I18n.t('LootGemQuestView_times_up')}</h3>
        </div>
      );
    }

    return (
      <div className="countdownContainer">
        <h3>{I18n.t('LootGemQuestView_remaining_time')}</h3>
        <LootCountdown
          onEnd={() => this.setState({ countdownEnd: true })}
          value={expiresAt}
        />
      </div>
    );
  }

  renderUsernameInput(disabled) {
    const { isUsernameRequired } = this.props;

    if (disabled && !this.state.username) {
      return null;
    }

    return (
      isUsernameRequired && (
        <div>
          <div
            className={classNames({
              inputField: true,
              missingValue: !disabled && this.state.missingUsername,
            })}
          >
            {disabled && <p>{I18n.t('LootGemQuestView_username_label')}</p>}
            <FontAwesome
              name="user"
              size="2x"
            />
            <input
              disabled={disabled}
              onChange={this.onUsernameChanged.bind(this)}
              placeholder={I18n.t('LootGemQuestView_username_label')}
              type="text"
              value={this.state.username}
            />
            {!disabled && this.state.missingUsername && (
              <FontAwesome
                className="missingValueIcon"
                name="exclamation-circle"
              />
            )}
          </div>
        </div>
      )
    );
  }

  renderSolution() {
    return (
      <div>
        {this.renderUsernameInput(true)}
        <img
          alt=""
          className="screenshot"
          src={this.state.screenshot}
        />
      </div>
    );
  }

  renderForm() {
    const { screenshot } = this.state;

    return (
      <div>
        {this.renderUsernameInput()}
        <div>
          <input
            accept=".png, .jpg, .jpeg .gif"
            id="fileInput"
            name="fileInput"
            onChange={(e) => this.fileUpload(e)}
            ref={(ref) => {
              this.myInput = ref;
            }}
            style={{ display: 'none' }}
            type="file"
          />
          <div className="buttonContainer">
            <LightButton
              className={classNames({
                missingValue: this.state.missingScreenshot,
              })}
              onClick={() => this.myInput.click()}
              title={I18n.t('LootGemQuestView_screenshot_label')}
            />
            {this.state.missingScreenshot && (
              <FontAwesome
                className="missingValueIcon"
                name="exclamation-circle"
              />
            )}
          </div>
          {screenshot && (
            <div className="screenshot">
              <img
                alt=""
                src={screenshot}
              />
              <FontAwesome
                className="trashIcon"
                name="times-circle"
                onClick={() =>
                  this.setState({
                    screenshot: '',
                    uploadObject: null,
                  })
                }
                size="2x"
              />
            </div>
          )}
        </div>
        <LootBoyButton
          className="submitButton"
          onClick={() => this.onSubmitButtonPressed()}
          title={I18n.t('ContactView_submit_button')}
        />
      </div>
    );
  }

  render() {
    const { status, reason, expiresAt } = this.props;

    const { countdownEnd } = this.state;

    const currentStatus =
      status === 'IN_PROGRESS' && isPastAndTruthy(expiresAt)
        ? 'EXPIRED'
        : status;

    return (
      <div className="questProgressContainer">
        {this.renderCountdown()}
        <h3>
          {I18n.t('LootGemQuestView_current_quest_status')}
          {': '}
          {I18n.t(LootGemQuestView.STATUS_MAPPING[currentStatus])}
        </h3>
        {status === 'REJECTED' && reason && (
          <p>
            <strong>
              {I18n.t('LootGemQuestView_reason_label')}
              {':'}
            </strong>{' '}
            {reason}
          </p>
        )}
        <div className="inputArea">
          {currentStatus === 'IN_PROGRESS' && (!expiresAt || !countdownEnd)
            ? this.renderForm()
            : null}
          {currentStatus !== 'IN_PROGRESS' &&
          currentStatus !== 'CANCELLED' &&
          currentStatus !== 'EXPIRED'
            ? this.renderSolution()
            : null}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = {
  sendQuestSolution,
  pushDialog,
};

export default withAlert()(connect(null, mapDispatchToProps)(LootGemQuestView));
